import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';

import api from '../lib/api';

// CRUZZN_XERO_EMPLOYEE_UI_V31B

const formatDateTime = (value) => {
  if (!value) {
    return 'Not available';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('en-AU', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const getErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;

  if (typeof detail === 'string') {
    return detail;
  }

  if (detail?.message) {
    return detail.message;
  }

  if (error?.response?.status === 401) {
    return 'Your session has expired. Please sign in again.';
  }

  if (error?.response?.status === 403) {
    return 'Administrator access is required to manage the Xero connection.';
  }

  return fallback;
};

export default function XeroIntegration() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [message, setMessage] = useState(null);

  const [employeePreview, setEmployeePreview] = useState(null);
  const [employeeHistory, setEmployeeHistory] = useState([]);
  const [previewingEmployees, setPreviewingEmployees] = useState(false);
  const [linkingEmployees, setLinkingEmployees] = useState(false);
  const [importingEmployees, setImportingEmployees] = useState(false);
  const [employeeMessage, setEmployeeMessage] = useState(null);

  const loadStatus = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    }

    try {
      const response = await api.get('/xero/status');
      setStatus(response.data);
    } catch (error) {
      setMessage({
        type: 'error',
        text: getErrorMessage(
          error,
          'Unable to retrieve the current Xero connection status.',
        ),
      });
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const xeroResult = query.get('xero');
    const oauthStatus = query.get('status');

    if (xeroResult === 'connected' && oauthStatus === 'success') {
      setMessage({
        type: 'success',
        text: 'Xero was connected successfully.',
      });
    } else if (
      xeroResult === 'connected'
      && oauthStatus === 'denied'
    ) {
      setMessage({
        type: 'error',
        text: 'Xero authorisation was cancelled or denied.',
      });
    } else if (
      xeroResult === 'connected'
      && oauthStatus
    ) {
      setMessage({
        type: 'error',
        text: 'Xero could not be connected. Please try again.',
      });
    }

    if (xeroResult || oauthStatus) {
      query.delete('xero');
      query.delete('status');

      const cleanQuery = query.toString();
      const cleanUrl = `${window.location.pathname}${
        cleanQuery ? `?${cleanQuery}` : ''
      }${window.location.hash}`;

      window.history.replaceState(
        {},
        document.title,
        cleanUrl,
      );
    }

    loadStatus();
  }, [loadStatus]);

  const handleConnect = async () => {
    setConnecting(true);
    setMessage(null);

    try {
      const response = await api.get('/xero/oauth/connect');
      const authorizationUrl = response.data?.authorization_url;

      if (!authorizationUrl) {
        throw new Error(
          'The Xero authorisation URL was not returned.',
        );
      }

      window.location.assign(authorizationUrl);
    } catch (error) {
      setMessage({
        type: 'error',
        text: getErrorMessage(
          error,
          'Unable to start the Xero connection.',
        ),
      });
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    const confirmed = window.confirm(
      'Disconnect Xero from Cruzzn Care?\n\n'
      + 'Existing Xero identifiers will remain in Cruzzn Care, '
      + 'but live Xero access and exports will stop until Xero '
      + 'is connected again.',
    );

    if (!confirmed) {
      return;
    }

    setDisconnecting(true);
    setMessage(null);

    try {
      await api.post('/xero/disconnect');

      setMessage({
        type: 'success',
        text: 'Xero has been disconnected.',
      });

      await loadStatus({ silent: true });
    } catch (error) {
      setMessage({
        type: 'error',
        text: getErrorMessage(
          error,
          'Unable to disconnect Xero.',
        ),
      });
    } finally {
      setDisconnecting(false);
    }
  };

  const loadEmployeeHistory = useCallback(async () => {
    try {
      const response = await api.get(
        '/xero/employees/sync-history',
        {
          params: {
            limit: 5,
          },
        },
      );

      setEmployeeHistory(
        Array.isArray(response.data?.records)
          ? response.data.records
          : [],
      );
    } catch (error) {
      // History is supplementary and must not block employee sync.
      setEmployeeHistory([]);
    }
  }, []);

  const handlePreviewEmployees = async ({
    silent = false,
  } = {}) => {
    if (!silent) {
      setPreviewingEmployees(true);
      setEmployeeMessage(null);
    }

    try {
      const response = await api.get(
        '/xero/employees/preview',
      );

      setEmployeePreview(response.data);

      if (!silent) {
        setEmployeeMessage({
          type: 'success',
          text: (
            'Xero employee preview completed. '
            + 'Review the results before linking or importing.'
          ),
        });
      }

      await loadEmployeeHistory();
    } catch (error) {
      setEmployeeMessage({
        type: 'error',
        text: getErrorMessage(
          error,
          'Unable to retrieve employees from Xero.',
        ),
      });
    } finally {
      if (!silent) {
        setPreviewingEmployees(false);
      }
    }
  };

  const handleLinkExistingEmployees = async () => {
    const linksRequired = Number(
      employeePreview?.summary?.links_required_count || 0,
    );

    if (linksRequired === 0) {
      setEmployeeMessage({
        type: 'error',
        text: (
          'There are no existing Cruzzn Care staff records '
          + 'requiring a Xero employee link.'
        ),
      });
      return;
    }

    const confirmed = window.confirm(
      `Link ${linksRequired} matched staff record${
        linksRequired === 1 ? '' : 's'
      } to Xero?\n\n`
      + 'This will only save the Xero Employee ID against '
      + 'the matched Cruzzn Care staff record. It will not '
      + 'overwrite names, email addresses, roles or payroll settings.',
    );

    if (!confirmed) {
      return;
    }

    setLinkingEmployees(true);
    setEmployeeMessage(null);

    try {
      const response = await api.post(
        '/xero/employees/sync',
      );

      const summary = response.data?.summary || {};

      setEmployeeMessage({
        type: 'success',
        text: (
          `Employee linking completed. ${
            summary.linked_count || 0
          } staff record${
            Number(summary.linked_count || 0) === 1 ? '' : 's'
          } linked.`
        ),
      });

      await handlePreviewEmployees({
        silent: true,
      });
      await loadEmployeeHistory();
    } catch (error) {
      setEmployeeMessage({
        type: 'error',
        text: getErrorMessage(
          error,
          'Unable to link Cruzzn Care staff to Xero employees.',
        ),
      });
    } finally {
      setLinkingEmployees(false);
    }
  };

  const handleImportEmployees = async () => {
    const importableCount = Number(
      employeePreview?.summary?.importable_count || 0,
    );

    const linksRequired = Number(
      employeePreview?.summary?.links_required_count || 0,
    );

    if (importableCount === 0 && linksRequired === 0) {
      setEmployeeMessage({
        type: 'error',
        text: (
          'There are no new Xero employees to import '
          + 'or existing staff records to link.'
        ),
      });
      return;
    }

    const confirmed = window.confirm(
      `Import ${importableCount} new Xero employee${
        importableCount === 1 ? '' : 's'
      } and link ${linksRequired} matched staff record${
        linksRequired === 1 ? '' : 's'
      }?\n\n`
      + 'New employees will be created as inactive staff. '
      + 'They will not receive an invitation and cannot log in '
      + 'until you review and activate them.',
    );

    if (!confirmed) {
      return;
    }

    setImportingEmployees(true);
    setEmployeeMessage(null);

    try {
      const response = await api.post(
        '/xero/employees/sync',
        null,
        {
          params: {
            import_unmatched: true,
          },
        },
      );

      const summary = response.data?.summary || {};

      setEmployeeMessage({
        type: 'success',
        text: (
          `Employee synchronisation completed. ${
            summary.imported_count || 0
          } employee${
            Number(summary.imported_count || 0) === 1 ? '' : 's'
          } imported and ${
            summary.linked_count || 0
          } existing staff record${
            Number(summary.linked_count || 0) === 1 ? '' : 's'
          } linked.`
        ),
      });

      await handlePreviewEmployees({
        silent: true,
      });
      await loadEmployeeHistory();
    } catch (error) {
      setEmployeeMessage({
        type: 'error',
        text: getErrorMessage(
          error,
          'Unable to import employees from Xero.',
        ),
      });
    } finally {
      setImportingEmployees(false);
    }
  };

  const configured = Boolean(status?.configured);
  const connected = Boolean(status?.connected);

  return (
    <section
      className="rounded-xl border border-border bg-card shadow-sm"
      data-testid="xero-integration-card"
    >
      <div className="flex flex-col gap-4 border-b border-border p-5 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold text-foreground">
              Xero Integration
            </h2>

            {!loading && (
              <span
                className={[
                  'inline-flex items-center rounded-full px-2.5 py-1',
                  'text-xs font-medium',
                  connected
                    ? 'bg-emerald-100 text-emerald-800'
                    : 'bg-muted text-muted-foreground',
                ].join(' ')}
                data-testid="xero-connection-status"
              >
                {connected ? 'Connected' : 'Not connected'}
              </span>
            )}
          </div>

          <p className="mt-1 text-sm text-muted-foreground">
            Connect Cruzzn Care to Xero Payroll to synchronise
            employees, earnings rates and approved timesheets.
          </p>
        </div>

        {!loading && configured && (
          connected ? (
            <button
              type="button"
              onClick={handleDisconnect}
              disabled={disconnecting}
              className={[
                'inline-flex min-h-10 items-center justify-center',
                'rounded-md border border-destructive px-4 py-2',
                'text-sm font-medium text-destructive',
                'transition-colors hover:bg-destructive/10',
                'disabled:cursor-not-allowed disabled:opacity-50',
              ].join(' ')}
              data-testid="disconnect-xero-button"
            >
              {disconnecting ? 'Disconnecting…' : 'Disconnect'}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleConnect}
              disabled={connecting}
              className={[
                'inline-flex min-h-10 items-center justify-center',
                'rounded-md bg-primary px-4 py-2',
                'text-sm font-medium text-primary-foreground',
                'transition-colors hover:bg-primary/90',
                'disabled:cursor-not-allowed disabled:opacity-50',
              ].join(' ')}
              data-testid="connect-xero-button"
            >
              {connecting ? 'Opening Xero…' : 'Connect to Xero'}
            </button>
          )
        )}
      </div>

      <div className="space-y-4 p-5">
        {message && (
          <div
            className={[
              'rounded-lg border px-4 py-3 text-sm',
              message.type === 'success'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                : 'border-red-200 bg-red-50 text-red-900',
            ].join(' ')}
            role="status"
            data-testid={`xero-${message.type}-message`}
          >
            {message.text}
          </div>
        )}

        {loading ? (
          <div
            className="space-y-3"
            data-testid="xero-status-loading"
          >
            <div className="h-4 w-48 animate-pulse rounded bg-muted" />
            <div className="h-4 w-64 animate-pulse rounded bg-muted" />
            <div className="h-4 w-56 animate-pulse rounded bg-muted" />
          </div>
        ) : !status ? (
          <p className="text-sm text-muted-foreground">
            Xero status is currently unavailable.
          </p>
        ) : !configured ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
            <p className="font-medium">
              Xero has not been fully configured on the server.
            </p>

            {status.missing_configuration?.length > 0 && (
              <p className="mt-2">
                Missing configuration:{' '}
                {status.missing_configuration.join(', ')}
              </p>
            )}
          </div>
        ) : connected ? (
          <>
            <dl className="grid gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Organisation
              </dt>
              <dd
                className="mt-1 text-sm font-medium text-foreground"
                data-testid="xero-tenant-name"
              >
                {status.tenant_name || 'Connected organisation'}
              </dd>
            </div>

            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Organisation type
              </dt>
              <dd className="mt-1 text-sm text-foreground">
                {status.tenant_type || 'Not available'}
              </dd>
            </div>

            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Connected by
              </dt>
              <dd
                className="mt-1 break-all text-sm text-foreground"
                data-testid="xero-connected-by"
              >
                {status.connected_by_email || 'Not recorded'}
              </dd>
            </div>

            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Last updated
              </dt>
              <dd
                className="mt-1 text-sm text-foreground"
                data-testid="xero-updated-at"
              >
                {formatDateTime(status.updated_at)}
              </dd>
            </div>

            <div className="sm:col-span-2">
              <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Xero tenant ID
              </dt>
              <dd className="mt-1 break-all font-mono text-xs text-muted-foreground">
                {status.tenant_id || 'Not available'}
              </dd>
            </div>
            </dl>

            <div
              className="border-t border-border pt-5"
              data-testid="xero-employee-sync-panel"
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h3 className="text-base font-semibold text-foreground">
                    Xero Employees
                  </h3>

                  <p className="mt-1 text-sm text-muted-foreground">
                    Preview Xero Payroll employees, link matching staff,
                    or import new employees as inactive Cruzzn Care staff.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => handlePreviewEmployees()}
                  disabled={
                    previewingEmployees
                    || linkingEmployees
                    || importingEmployees
                  }
                  className={[
                    'inline-flex min-h-10 items-center justify-center',
                    'rounded-md border border-border px-4 py-2',
                    'text-sm font-medium text-foreground',
                    'transition-colors hover:bg-muted',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                  ].join(' ')}
                  data-testid="preview-xero-employees-button"
                >
                  {previewingEmployees
                    ? 'Loading employees…'
                    : employeePreview
                      ? 'Refresh Preview'
                      : 'Preview Employees'}
                </button>
              </div>

              {employeeMessage && (
                <div
                  className={[
                    'mt-4 rounded-lg border px-4 py-3 text-sm',
                    employeeMessage.type === 'success'
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                      : 'border-red-200 bg-red-50 text-red-900',
                  ].join(' ')}
                  role="status"
                  data-testid={`xero-employee-${employeeMessage.type}-message`}
                >
                  {employeeMessage.text}
                </div>
              )}

              {!employeePreview ? (
                <div className="mt-4 rounded-lg border border-dashed border-border p-5 text-sm text-muted-foreground">
                  Select <strong>Preview Employees</strong> to compare
                  Xero Payroll employees with Cruzzn Care staff.
                </div>
              ) : (
                <div className="mt-5 space-y-5">
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    {[
                      {
                        label: 'Employees in Xero',
                        value: employeePreview.summary?.xero_employee_count || 0,
                      },
                      {
                        label: 'Existing matches',
                        value: employeePreview.summary?.matched_count || 0,
                      },
                      {
                        label: 'Ready to import',
                        value: employeePreview.summary?.importable_count || 0,
                      },
                      {
                        label: 'Blocked records',
                        value: employeePreview.summary?.blocked_count || 0,
                      },
                    ].map((item) => (
                      <div
                        key={item.label}
                        className="rounded-lg border border-border bg-background p-4"
                      >
                        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          {item.label}
                        </p>

                        <p className="mt-2 text-2xl font-semibold text-foreground">
                          {item.value}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="grid gap-3 sm:grid-cols-3">
                    <div className="rounded-lg bg-muted/50 p-3">
                      <p className="text-xs text-muted-foreground">
                        Already linked
                      </p>
                      <p className="mt-1 text-lg font-semibold text-foreground">
                        {employeePreview.summary?.already_linked_count || 0}
                      </p>
                    </div>

                    <div className="rounded-lg bg-muted/50 p-3">
                      <p className="text-xs text-muted-foreground">
                        Links required
                      </p>
                      <p className="mt-1 text-lg font-semibold text-foreground">
                        {employeePreview.summary?.links_required_count || 0}
                      </p>
                    </div>

                    <div className="rounded-lg bg-muted/50 p-3">
                      <p className="text-xs text-muted-foreground">
                        Unmatched Cruzzn Care staff
                      </p>
                      <p className="mt-1 text-lg font-semibold text-foreground">
                        {employeePreview.summary?.unmatched_staff_count || 0}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 sm:flex-row">
                    <button
                      type="button"
                      onClick={handleLinkExistingEmployees}
                      disabled={
                        linkingEmployees
                        || importingEmployees
                        || Number(
                          employeePreview.summary?.links_required_count || 0,
                        ) === 0
                      }
                      className={[
                        'inline-flex min-h-10 items-center justify-center',
                        'rounded-md border border-primary px-4 py-2',
                        'text-sm font-medium text-primary',
                        'transition-colors hover:bg-primary/10',
                        'disabled:cursor-not-allowed disabled:opacity-50',
                      ].join(' ')}
                      data-testid="link-xero-employees-button"
                    >
                      {linkingEmployees
                        ? 'Linking staff…'
                        : 'Link Existing Staff'}
                    </button>

                    <button
                      type="button"
                      onClick={handleImportEmployees}
                      disabled={
                        linkingEmployees
                        || importingEmployees
                        || (
                          Number(
                            employeePreview.summary?.importable_count || 0,
                          ) === 0
                          && Number(
                            employeePreview.summary?.links_required_count || 0,
                          ) === 0
                        )
                      }
                      className={[
                        'inline-flex min-h-10 items-center justify-center',
                        'rounded-md bg-primary px-4 py-2',
                        'text-sm font-medium text-primary-foreground',
                        'transition-colors hover:bg-primary/90',
                        'disabled:cursor-not-allowed disabled:opacity-50',
                      ].join(' ')}
                      data-testid="import-xero-employees-button"
                    >
                      {importingEmployees
                        ? 'Importing employees…'
                        : 'Import New Employees'}
                    </button>
                  </div>

                  {employeePreview.importable_xero_employees?.length > 0 && (
                    <div className="overflow-hidden rounded-lg border border-border">
                      <div className="border-b border-border bg-muted/40 px-4 py-3">
                        <h4 className="text-sm font-semibold text-foreground">
                          Employees ready to import
                        </h4>
                      </div>

                      <div className="max-h-80 overflow-auto">
                        <table className="w-full text-left text-sm">
                          <thead className="sticky top-0 bg-background">
                            <tr className="border-b border-border">
                              <th className="px-4 py-3 font-medium text-muted-foreground">
                                Employee
                              </th>
                              <th className="px-4 py-3 font-medium text-muted-foreground">
                                Email
                              </th>
                              <th className="px-4 py-3 font-medium text-muted-foreground">
                                Xero status
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {employeePreview.importable_xero_employees.map(
                              (employee) => (
                                <tr
                                  key={employee.xero_employee_id}
                                  className="border-b border-border last:border-b-0"
                                >
                                  <td className="px-4 py-3 font-medium text-foreground">
                                    {employee.full_name || 'Unnamed employee'}
                                  </td>
                                  <td className="break-all px-4 py-3 text-muted-foreground">
                                    {employee.email || 'No email'}
                                  </td>
                                  <td className="px-4 py-3 text-muted-foreground">
                                    {employee.status || 'Not available'}
                                  </td>
                                </tr>
                              ),
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {employeePreview.blocked_xero_employees?.length > 0 && (
                    <div className="overflow-hidden rounded-lg border border-amber-300">
                      <div className="border-b border-amber-300 bg-amber-50 px-4 py-3">
                        <h4 className="text-sm font-semibold text-amber-950">
                          Employees requiring manual review
                        </h4>
                      </div>

                      <div className="divide-y divide-amber-200">
                        {employeePreview.blocked_xero_employees.map(
                          (row, index) => {
                            const employee = row.xero_employee || {};

                            return (
                              <div
                                key={
                                  employee.xero_employee_id
                                  || `${row.reason}-${index}`
                                }
                                className="px-4 py-3 text-sm"
                              >
                                <p className="font-medium text-foreground">
                                  {employee.full_name || 'Unnamed employee'}
                                </p>
                                <p className="mt-1 break-all text-muted-foreground">
                                  {employee.email || 'No email address'}
                                </p>
                                <p className="mt-1 text-amber-900">
                                  Reason: {String(row.reason || 'manual_review')
                                    .replaceAll('_', ' ')}
                                </p>
                              </div>
                            );
                          },
                        )}
                      </div>
                    </div>
                  )}

                  <div className="rounded-lg border border-border bg-muted/30 p-4">
                    <h4 className="text-sm font-semibold text-foreground">
                      Import safeguards
                    </h4>

                    <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                      <li>
                        New employees are created as inactive staff.
                      </li>
                      <li>
                        No login invitation is sent automatically.
                      </li>
                      <li>
                        Names and emails are not overwritten on existing staff.
                      </li>
                      <li>
                        Missing or conflicting employee records are blocked.
                      </li>
                    </ul>
                  </div>

                  {employeeHistory.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-foreground">
                        Recent employee synchronisations
                      </h4>

                      <div className="mt-3 space-y-2">
                        {employeeHistory.map((record) => (
                          <div
                            key={record.id}
                            className="flex flex-col gap-1 rounded-lg border border-border px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between"
                          >
                            <div>
                              <p className="font-medium text-foreground">
                                {formatDateTime(record.completed_at)}
                              </p>
                              <p className="text-muted-foreground">
                                By {record.executed_by_email || 'Administrator'}
                              </p>
                            </div>

                            <p className="text-muted-foreground">
                              Imported {record.summary?.imported_count || 0}
                              {' · '}
                              Linked {record.summary?.linked_count || 0}
                              {' · '}
                              Skipped {record.summary?.skipped_count || 0}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">
              No Xero organisation is currently connected.
            </p>
            <p className="text-sm text-muted-foreground">
              Select Connect to Xero, sign in to Xero and approve
              access to the organisation used for payroll.
            </p>
          </div>
        )}

        {!loading && status?.redirect_uri && (
          <div className="border-t border-border pt-4">
            <p className="text-xs text-muted-foreground">
              Authorised redirect URI
            </p>
            <p className="mt-1 break-all font-mono text-xs text-foreground">
              {status.redirect_uri}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
